import { formatToCurrency } from "../../currency/format";
import { PriceReference } from "../../models/catalog-item";

interface CatalogItemPriceHintProps {
  currency: string;
  /** The last prices charged, this client first when there were any. */
  prices: number[];
  pricesForThisClient: boolean;
  /** Only set once a price was written and the history was consulted. */
  reference?: PriceReference;
}

/**
 * What was charged before, beside the price field and never inside it.
 *
 * Two separate things share this spot because they answer the same question at
 * different moments. The grey line is a reference: the catalogue fills in the
 * code, the tax rate and the unit, and this is the one field it deliberately
 * leaves empty, because a price belongs to the sale and a saved one goes stale
 * without anybody noticing. The warning below is the other half — it fires when
 * what was typed is an order of magnitude away from usual, which is the missing
 * zero it exists to catch, and it works with an empty catalogue because it
 * matches on the line's own description or code.
 */
const CatalogItemPriceHint: React.FC<CatalogItemPriceHintProps> = ({
  currency,
  prices,
  pricesForThisClient,
  reference,
}) => {
  const outOfRange = reference !== undefined && reference.outOfRange;

  if (prices.length === 0 && !outOfRange) {
    return <></>;
  }

  return (
    <>
      {prices.length > 0 && (
        <small className="form-text text-muted">
          {pricesForThisClient
            ? "Últimas veces a este cliente: "
            : "Últimas veces: "}
          {prices
            .map((price) => formatToCurrency(price, currency))
            .join(" · ")}
        </small>
      )}
      {outOfRange && (
        <small className="form-text text-danger">
          <i className="fa fa-exclamation-triangle mr-1"></i>
          Venías facturando esto a{" "}
          {formatToCurrency(reference!.median!, currency)}. Revisá el precio
          antes de guardar.
        </small>
      )}
    </>
  );
};

export default CatalogItemPriceHint;
